import { SearchableSelect } from "@/components/ui/searchable-select";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Download, FileSpreadsheet, RefreshCw, Undo2, Upload } from "lucide-react";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { openWorkbook, SPREADSHEET_ACCEPT, type Workbook } from "@/lib/workbook";
import { buildTemplateXlsx, downloadBlob } from "@/lib/xlsxWrite";
import { downloadCsv } from "@/lib/exportCsv";
import { formatDateTime } from "@/lib/dateTime";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusPill, StatusTabs, type Tone } from "@/components/patterns";

/** Rows the wizard takes from one file; the server checks CHECK_PART of them per request, so a bigger file is checked part by part */
const MAX_ROWS = 20000;
const CHECK_PART = 5000;
/** Rows sent per request while importing */
const BATCH = 250;

type FieldDef = { key: string; label: string; synonyms: string[] };
/** The CRM fields a file column can fill, with the column names they are recognised by */
const FIELDS: FieldDef[] = [
  { key: "full_name", label: "Full name (split into first and last)", synonyms: ["name", "fullname", "contactname", "contactperson", "customername", "personname"] },
  { key: "first_name", label: "First name", synonyms: ["firstname", "fname", "givenname"] },
  { key: "last_name", label: "Last name", synonyms: ["lastname", "lname", "surname", "familyname"] },
  { key: "company_name", label: "Company", synonyms: ["company", "companyname", "organisation", "organization", "firm", "businessname", "business", "account"] },
  { key: "mobile", label: "Mobile", synonyms: ["mobile", "mobileno", "mobilenumber", "cell", "cellphone", "whatsapp", "whatsappno"] },
  { key: "phone", label: "Landline", synonyms: ["tel", "telephoneno", "landlineno", "phone", "phoneno", "phonenumber", "telephone", "landline", "contactnumber", "contactno"] },
  { key: "alternate_phone", label: "Another number", synonyms: ["alternatephone", "altphone", "otherphone", "phone2", "anothernumber", "othernumber"] },
  { key: "email", label: "Email", synonyms: ["email", "emailid", "emailaddress", "mail"] },
  { key: "job_title", label: "Job title", synonyms: ["jobtitle", "designation", "title", "position", "role"] },
  { key: "industry", label: "Industry", synonyms: ["industry", "sector"] },
  { key: "company_size", label: "Company size", synonyms: ["companysize", "size", "employees", "headcount"] },
  { key: "website", label: "Website", synonyms: ["website", "web", "url", "site"] },
  { key: "city", label: "City", synonyms: ["city", "town", "location"] },
  { key: "state", label: "State", synonyms: ["state", "province"] },
  { key: "region", label: "Region", synonyms: ["region", "territory", "zone"] },
  { key: "source", label: "Source", synonyms: ["source", "leadsource", "channel"] },
  { key: "campaign", label: "Campaign", synonyms: ["campaign", "campaignname", "utmcampaign"] },
  { key: "referred_by", label: "Referred by", synonyms: ["referredby", "referrer", "reference"] },
  { key: "product_interest", label: "Product of interest", synonyms: ["product", "productinterest", "interest", "requirement", "service"] },
  { key: "expected_budget", label: "Budget (₹)", synonyms: ["budget", "expectedbudget", "value", "amount"] },
  { key: "expected_timeline", label: "Timeline", synonyms: ["timeline", "expectedtimeline", "timeframe"] },
  { key: "requirement_notes", label: "Notes", synonyms: ["notes", "note", "remarks", "comments", "comment", "message", "requirementnotes", "description"] },
  { key: "priority", label: "Priority (Hot, Warm, Cold; blank = Cold. \"Follow-up\" goes to the Follow-up stage)", synonyms: ["priority", "rating", "temperature"] },
  { key: "lead_status", label: "Stage (New, Contacted, Follow-up, Qualified; blank = New)", synonyms: ["stage", "status", "leadstatus", "leadstage", "funnelstage"] },
  { key: "next_follow_up_at", label: "Next follow-up date", synonyms: ["followupdate", "nextfollowup", "nextfollowupdate", "nextfollowupon", "followupon", "callbackdate", "nextcall", "nextcalldate"] },
  { key: "tags", label: "Tags (separated by ;)", synonyms: ["tags", "tag", "labels"] },
  { key: "owner_email", label: "Owner's email", synonyms: ["owner", "owneremail", "assignedto", "salesrep", "rep", "salesperson"] },
];
const squash = (v: string) => v.toLowerCase().replace(/[^a-z0-9]/g, "");
const fieldFor = (header: string): string => {
  const h = squash(header);
  return FIELDS.find((f) => squash(f.key) === h || f.synonyms.includes(h))?.key || "";
};

type Source = { code: string; name: string };
type Rep = { id: string; first_name: string; last_name?: string; user_role?: { name: string } };
type OwnerMode = "rules" | "one" | "turns" | "column" | "unassigned";
type CheckRow = { line: number; status: "ready" | "duplicate" | "error"; problems: string[]; owner: string | null; stage?: string; notes?: string[] };
type FollowUps = { total: number; from_priority: number };
type Checked = { rows: CheckRow[]; counts: { total: number; ready: number; duplicate: number; error: number }; will_import: number; follow_up: FollowUps };
type Done = { id: string; created: number; skipped: number; failed: number; problems: Map<number, string> };
type HistoryRow = { id: string; file_name: string; created_on: string; created_count: number; skipped_count: number; failed_count: number; undone_at: string | null; undone_count: number; can_undo: boolean; by: string | null };

const STEPS = ["Upload", "Match columns", "Choices", "Check & import"];
const selectClass = "h-9 w-full rounded-md border border-input bg-card px-2.5 text-[13px] outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/30";
const STATUS: Record<CheckRow["status"], { label: string; tone: Tone }> = { ready: { label: "Ready", tone: "success" }, duplicate: { label: "Duplicate", tone: "warning" }, error: { label: "Problem", tone: "danger" } };

function Card({ title, sub, children }: { title: string; sub?: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3 rounded-[10px] border border-border bg-card px-[18px] py-4">
      <div>
        <h2 className="text-sm font-semibold">{title}</h2>
        {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
      </div>
      {children}
    </section>
  );
}

function Choice({ name, value, current, onPick, label, note, disabled, children }: { name: string; value: string; current: string; onPick: (v: string) => void; label: string; note?: string; disabled?: boolean; children?: React.ReactNode }) {
  const on = value === current;
  return (
    <label className={cn("flex cursor-pointer flex-col gap-1 rounded-lg border px-3 py-2.5", on ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50", disabled && "cursor-not-allowed opacity-50")}>
      <span className="flex items-center gap-2 text-[13px] font-medium">
        <input type="radio" name={name} value={value} checked={on} disabled={disabled} onChange={() => onPick(value)} className="accent-primary" />
        {label}
      </span>
      {note && <span className="pl-6 text-xs text-muted-foreground">{note}</span>}
      {on && children && <div className="pl-6 pt-1">{children}</div>}
    </label>
  );
}

/** Leads → Import: upload a CSV or Excel file, match its columns, choose source, owner and what to do with duplicates, check every row, then import */
export function LeadImportPage() {
  const navigate = useNavigate();
  const { getScope } = useAuth();
  const { success, error } = useToast();
  const routesAll = getScope("lead") === "all";

  const [step, setStep] = useState(1);
  const [fileName, setFileName] = useState("");
  const [header, setHeader] = useState<string[]>([]);
  const [body, setBody] = useState<string[][]>([]);
  const [mapping, setMapping] = useState<string[]>([]);
  const [reading, setReading] = useState(false);
  const [book, setBook] = useState<Workbook | null>(null);
  const [sheetIndex, setSheetIndex] = useState(0);

  const [sources, setSources] = useState<Source[]>([]);
  const [reps, setReps] = useState<Rep[]>([]);
  const [history, setHistory] = useState<HistoryRow[]>([]);

  const [source, setSource] = useState("IMPORT");
  const [sourceMap, setSourceMap] = useState<Record<string, string>>({});
  const [campaign, setCampaign] = useState("");
  const [ownerMode, setOwnerMode] = useState<OwnerMode>("rules");
  const [ownerId, setOwnerId] = useState("");
  const [ownerIds, setOwnerIds] = useState<string[]>([]);
  const [duplicates, setDuplicates] = useState<"skip" | "create">("skip");

  const [checking, setChecking] = useState(false);
  const [checkNote, setCheckNote] = useState("");
  const [checked, setChecked] = useState<Checked | null>(null);
  const [view, setView] = useState("all");
  const [progress, setProgress] = useState<number | null>(null);
  const [done, setDone] = useState<Done | null>(null);

  const loadHistory = () => api.get("/api/Lead/Import/History").then((r) => setHistory(unwrapArray<HistoryRow>(r))).catch(() => undefined);
  useEffect(() => {
    api.get("/api/MasterLookup/ByCategory/LEAD_SOURCE").then((r) => setSources(unwrapArray<Source>(r))).catch(() => undefined);
    api.get("/api/User/Assignable?for=lead").then((r) => setReps(unwrapArray<Rep>(r))).catch(() => undefined);
    void loadHistory();
  }, []);

  const mapped = (key: string) => mapping.includes(key);
  const sourceColumn = mapping.indexOf("source");
  const fileSources = useMemo(() => (sourceColumn < 0 ? [] : [...new Set(body.map((r) => (r[sourceColumn + 1] || "").trim()).filter(Boolean))].slice(0, 50)), [body, sourceColumn]);
  const repName = (r: Rep) => [r.first_name, r.last_name].filter(Boolean).join(" ");

  /** Takes one sheet's cells as the file to import: the header row, the data rows with their line numbers, and a first guess at the column matches */
  const loadSheet = async (wb: Workbook, index: number, name: string) => {
    const all = (await wb.read(index)).map((r) => r.map((c) => String(c ?? "").trim()));
    // The header is the first row with something in it; data rows keep their line number in the file
    const start = all.findIndex((r) => r.some(Boolean));
    if (start < 0) throw new Error(wb.sheets.length > 1 ? `The sheet "${wb.sheets[index]}" is empty` : "The file is empty");
    const head = all[start]!.map((h, i) => h || `Column ${i + 1}`);
    const rows: string[][] = [];
    const lines: number[] = [];
    all.slice(start + 1).forEach((r, i) => {
      if (r.some(Boolean)) { rows.push(r); lines.push(start + i + 2); }
    });
    if (!rows.length) throw new Error("The file has a header row but no leads under it");
    if (rows.length > MAX_ROWS) throw new Error(`The file has ${rows.length} rows; import at most ${MAX_ROWS.toLocaleString("en-IN")} at a time (split the file)`);
    setFileName(name);
    setHeader(head);
    setBody(rows.map((r, i) => [String(lines[i]), ...r]));
    // Each field is matched to the first column that looks like it
    const used = new Set<string>();
    setMapping(head.map((h) => { const f = fieldFor(h); if (!f || used.has(f)) return ""; used.add(f); return f; }));
    setSheetIndex(index);
    setChecked(null);
    setDone(null);
  };

  const readFile = async (file?: File) => {
    if (!file) return;
    setReading(true);
    try {
      const wb = await openWorkbook(file);
      // Start on the first sheet that has something in it
      let first = 0;
      for (let i = 0; i < wb.sheets.length; i++) {
        if ((await wb.read(i)).some((r) => r.some((c) => String(c ?? "").trim()))) { first = i; break; }
      }
      await loadSheet(wb, first, file.name);
      setBook(wb);
      setStep(2);
    } catch (err: any) {
      error("Can't read the file", err?.message || "Please check the file and try again");
    } finally {
      setReading(false);
    }
  };

  /** Another sheet of the same workbook: the column matches are made again from its header */
  const chooseSheet = async (index: number) => {
    if (!book || index === sheetIndex) return;
    setReading(true);
    try {
      await loadSheet(book, index, fileName);
    } catch (err: any) {
      error("Can't use that sheet", err?.message || "Choose another sheet");
    } finally {
      setReading(false);
    }
  };

  /** The rows as the server takes them: CRM fields, the line in the file, the source mapped to a lead source code */
  const rowsForServer = () =>
    body.map((r) => {
      const line = Number(r[0]);
      const row: Record<string, any> = { line };
      mapping.forEach((key, i) => {
        if (!key) return;
        const v = (r[i + 1] || "").trim();
        if (!v) return;
        if (key === "tags") row.tags = v.split(/[;,]/).map((t) => t.trim()).filter(Boolean);
        else if (key === "source") row.source = sourceMap[v] || undefined;
        else row[key] = v;
      });
      return row;
    });

  const options = () => ({
    source,
    campaign: campaign.trim() || undefined,
    owner_mode: ownerMode,
    owner_id: ownerMode === "one" ? ownerId : undefined,
    owner_ids: ownerMode === "turns" ? ownerIds : undefined,
    duplicates,
  });

  const columnsOk = (mapped("first_name") || mapped("full_name")) && mapped("company_name");
  const choicesProblem =
    !source ? "Choose a source" :
    fileSources.some((v) => !sourceMap[v]) ? "Match every source in the file to one of your lead sources" :
    ownerMode === "one" && !ownerId ? "Choose who gets the leads" :
    ownerMode === "turns" && ownerIds.length < 2 ? "Choose at least two people to take turns" :
    ownerMode === "column" && !mapped("owner_email") ? "Match a column to Owner's email first" : "";

  // Sources in the file are matched to the list by code or name where possible
  useEffect(() => {
    if (!fileSources.length || !sources.length) return;
    setSourceMap((m) => {
      const next = { ...m };
      for (const v of fileSources) {
        if (next[v]) continue;
        const hit = sources.find((s) => squash(s.code) === squash(v) || squash(s.name) === squash(v) || squash(s.name).startsWith(squash(v)));
        if (hit) next[v] = hit.code;
      }
      return next;
    });
  }, [fileSources, sources]);

  const runCheck = async () => {
    setChecking(true);
    setChecked(null);
    setDone(null);
    setCheckNote("");
    setStep(4);
    try {
      // A big file is checked in parts; each part is told what the earlier ones held, so repeats inside the file are still found
      const rows = rowsForServer();
      const parts = Math.ceil(rows.length / CHECK_PART);
      const all: CheckRow[] = [];
      const follow_up: FollowUps = { total: 0, from_priority: 0 };
      let seen: Record<string, number> = {};
      for (let p = 0; p < parts; p++) {
        if (parts > 1) setCheckNote(`Checking part ${p + 1} of ${parts}…`);
        const res = unwrapObject<Checked & { keys?: Record<string, number> }>(await api.post("/api/Lead/Import/Check", { rows: rows.slice(p * CHECK_PART, (p + 1) * CHECK_PART), options: options(), seen: p ? seen : undefined }));
        all.push(...res.rows);
        for (const k of ["total", "from_priority"] as const) follow_up[k] += res.follow_up?.[k] || 0;
        seen = { ...seen, ...(res.keys || {}) };
      }
      const count = (st: CheckRow["status"]) => all.filter((r) => r.status === st).length;
      setChecked({
        rows: all,
        counts: { total: all.length, ready: count("ready"), duplicate: count("duplicate"), error: count("error") },
        will_import: count("ready") + (duplicates === "create" ? count("duplicate") : 0),
        follow_up,
      });
    } catch (err: any) {
      error("Couldn't check the file", err?.message || "Please try again");
      setStep(3);
    } finally {
      setChecking(false);
      setCheckNote("");
    }
  };

  const runImport = async () => {
    if (!checked) return;
    const take = new Set(checked.rows.filter((r) => r.status === "ready" || (duplicates === "create" && r.status === "duplicate")).map((r) => r.line));
    const rows = rowsForServer().filter((r) => take.has(r.line));
    const problems = new Map<number, string>();
    checked.rows.filter((r) => !take.has(r.line)).forEach((r) => problems.set(r.line, r.problems.join("; ")));
    let created = 0, skipped = checked.rows.filter((r) => r.status === "duplicate" && !take.has(r.line)).length, failed = checked.counts.error;
    setProgress(0);
    try {
      const start = unwrapObject<{ id: string }>(await api.post("/api/Lead/Import/Start", { file_name: fileName, row_count: rows.length, options: options() }));
      for (let i = 0; i < rows.length; i += BATCH) {
        const res = unwrapObject<{ results: Array<{ line: number; status: string; reason?: string }> }>(await api.post(`/api/Lead/Import/${start.id}/Rows`, { rows: rows.slice(i, i + BATCH) }));
        for (const x of res.results || []) {
          if (x.status === "created") created++;
          else { if (x.status === "skipped") skipped++; else failed++; problems.set(x.line, x.reason || "Not imported"); }
        }
        setProgress(Math.min(100, Math.round(((i + BATCH) / rows.length) * 100)));
      }
      await api.post(`/api/Lead/Import/${start.id}/Finish`, { skipped, failed });
      setDone({ id: start.id, created, skipped, failed, problems });
      success("Import finished", `${created} lead${created === 1 ? "" : "s"} added`);
      void loadHistory();
    } catch (err: any) {
      error("The import stopped", err?.message || "Some rows may have been added; see the import history");
    } finally {
      setProgress(null);
    }
  };

  const downloadProblems = () => {
    if (!done) return;
    const bad = body.filter((r) => done.problems.has(Number(r[0])));
    downloadCsv(`${fileName.replace(/\.(csv|xlsx|xls)$/i, "")}-problems.csv`, [["Line", (r: string[]) => r[0]], ...header.map((h, i) => [h, (r: string[]) => r[i + 1] || ""] as [string, (r: string[]) => string]), ["Problem", (r: string[]) => done.problems.get(Number(r[0])) || ""]], bad);
  };

  const undo = async (id: string) => {
    if (!window.confirm("Remove the leads this import added? Leads someone has already worked on are kept.")) return;
    try {
      const r = unwrapObject<{ removed: number; kept: number }>(await api.post(`/api/Lead/Import/${id}/Undo`));
      success("Import undone", `${r.removed} removed${r.kept ? `, ${r.kept} kept because someone has worked on them` : ""}`);
      if (done?.id === id) setDone(null);
      void loadHistory();
    } catch (err: any) {
      error("Couldn't undo", err?.message || "Please try again");
    }
  };

  const downloadTemplate = () => {
    const headers = ["First name", "Last name", "Company", "Mobile", "Landline", "Another number", "Email", "Website", "Job title", "City", "State", "Source", "Campaign", "Product of interest", "Budget", "Notes", "Priority", "Stage", "Next follow-up date", "Owner email"];
    downloadBlob("lead-import-template.xlsx", buildTemplateXlsx({
      headers,
      example: { "First name": "Ravi", "Last name": "Iyer", Company: "Sri Lakshmi Mills", Mobile: "98430 11287", Landline: "0422 2345678", Email: "ravi@example.com", Website: "https://srilakshmimills.example.com", City: "Coimbatore", State: "Tamil Nadu", Source: (sources.find((s) => /trade/i.test(s.name)) || sources[0])?.name || "", Priority: "Cold", Stage: "New" },
      // Dropdowns: the lead sources of this CRM, the three priorities and the stages a lead can start in
      choices: { Source: sources.map((s) => s.name), Priority: ["Hot", "Warm", "Cold"], Stage: ["New", "Contacted", "Follow-up", "Qualified"] },
      // Text, so a leading 0 (landline STD code) is kept and a date such as 25/12/2026 stays as typed
      textColumns: ["Mobile", "Landline", "Another number", "Next follow-up date"],
      rows: MAX_ROWS + 1,
    }));
  };

  const shown = (checked?.rows || []).filter((r) => view === "all" || r.status === view);
  const lineData = useMemo(() => new Map(body.map((r) => [Number(r[0]), r])), [body]);
  const cell = (line: number, key: string) => { const i = mapping.indexOf(key); return i < 0 ? "" : lineData.get(line)?.[i + 1] || ""; };

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Link to="/leads" className="mb-1 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"><ArrowLeft className="size-3.5" />Leads</Link>
          <h1 className="text-[19px] font-semibold tracking-tight">Import leads</h1>
        </div>
        <ol className="flex flex-wrap items-center gap-1.5 text-[12.5px]" aria-label="Steps">
          {STEPS.map((s, i) => (
            <li key={s} aria-current={step === i + 1 ? "step" : undefined} className={cn("flex items-center gap-1.5 rounded-full px-2.5 py-1", step === i + 1 ? "bg-primary text-primary-foreground" : step > i + 1 ? "bg-success-soft text-success" : "bg-muted text-muted-foreground")}>
              <span className="font-semibold">{i + 1}</span>{s}
            </li>
          ))}
        </ol>
      </div>

      {step === 1 && (
        <>
          <Card title="Choose a file" sub="A CSV or Excel file (.xlsx, or an older .xls) with one lead per row and the column names in the first row. Up to 20,000 rows.">
            <label
              className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border bg-muted/30 px-4 py-10 text-center hover:bg-muted/60"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); void readFile(e.dataTransfer.files?.[0]); }}
            >
              {reading ? <RefreshCw className="size-6 animate-spin text-primary" /> : <Upload className="size-6 text-muted-foreground" />}
              <span className="text-sm font-medium">{reading ? "Reading the file…" : "Drop the file here, or click to choose"}</span>
              <span className="text-xs text-muted-foreground">.csv, .xlsx or .xls · if the workbook has several sheets you choose one next</span>
              <input type="file" accept={SPREADSHEET_ACCEPT} className="sr-only" onChange={(e) => { void readFile(e.target.files?.[0]); e.target.value = ""; }} />
            </label>
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
              <span>Any column names work: you match them to CRM fields in the next step. The template has dropdowns for Source, Priority and Stage, and keeps phone numbers as Text. A lead in the Follow-up stage needs a Next follow-up date (day/month/year); without one it is due today.</span>
              <Button type="button" variant="outline" size="sm" onClick={downloadTemplate}><Download className="mr-1.5 size-4" />Excel template</Button>
            </div>
          </Card>
          {history.length > 0 && (
            <Card title="Recent imports" sub="An import can be undone for 24 hours. Leads someone has already worked on are kept.">
              <ul className="divide-y divide-border/60 text-[13px]">
                {history.map((h) => (
                  <li key={h.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
                    <FileSpreadsheet className="size-4 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1 truncate font-medium">{h.file_name}</span>
                    <span className="text-xs text-muted-foreground">{formatDateTime(h.created_on)}{h.by ? ` · ${h.by}` : ""}</span>
                    <span className="text-xs">{h.created_count} added{h.skipped_count ? ` · ${h.skipped_count} skipped` : ""}{h.failed_count ? ` · ${h.failed_count} failed` : ""}</span>
                    {h.undone_at ? <StatusPill tone="neutral">Undone ({h.undone_count} removed)</StatusPill> : h.can_undo ? <Button type="button" size="sm" variant="outline" onClick={() => undo(h.id)}><Undo2 className="mr-1.5 size-3.5" />Undo</Button> : null}
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </>
      )}

      {step === 2 && (
        <Card title={`Match columns · ${fileName}`} sub={`${body.length} row${body.length === 1 ? "" : "s"}. Choose the CRM field for each column you want; leave the rest as "Don't import".`}>
          {book && book.sheets.length > 1 && (
            <label className="flex flex-wrap items-center gap-2 text-[13px]">
              <span className="font-medium">Sheet to import</span>
              <SearchableSelect aria-label="Sheet to import" value={String(sheetIndex)} onChange={(e) => void chooseSheet(Number(e.target.value))} className={cn(selectClass, "w-[240px]")}>
                {book.sheets.map((n, i) => <option key={i} value={String(i)}>{n}</option>)}
              </SearchableSelect>
              <span className="text-xs text-muted-foreground">The workbook has {book.sheets.length} sheets. Choosing another one matches its columns again.</span>
            </label>
          )}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse text-[13px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="py-1.5 font-semibold">Column in the file</th>
                  <th className="py-1.5 font-semibold">First values</th>
                  <th className="w-[260px] py-1.5 font-semibold">CRM field</th>
                </tr>
              </thead>
              <tbody>
                {header.map((h, i) => (
                  <tr key={i} className="border-t border-border/60 align-top">
                    <td className="py-2 pr-3 font-medium">{h}</td>
                    <td className="max-w-[320px] py-2 pr-3 text-muted-foreground"><span className="line-clamp-2">{body.slice(0, 3).map((r) => r[i + 1]).filter(Boolean).join(" · ") || "—"}</span></td>
                    <td className="py-1.5">
                      <SearchableSelect aria-label={`CRM field for ${h}`} value={mapping[i] || ""} onChange={(e) => setMapping((m) => m.map((x, j) => (j === i ? e.target.value : x === e.target.value && e.target.value ? "" : x)))} className={selectClass}>
                        <option value="">Don't import</option>
                        {FIELDS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                      </SearchableSelect>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!columnsOk && <p className="text-xs text-warning">Match a column to First name (or Full name) and one to Company: every lead needs them.</p>}
          <div className="flex justify-between gap-2">
            <Button type="button" variant="outline" onClick={() => setStep(1)}>Choose another file</Button>
            <Button type="button" disabled={!columnsOk} onClick={() => setStep(3)}>Next: choices</Button>
          </div>
        </Card>
      )}

      {step === 3 && (
        <>
          <Card title="Source & campaign" sub="Reports count leads by where they came from.">
            {fileSources.length > 0 ? (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">The Source column has these values. Match each to one of your lead sources.</p>
                {fileSources.map((v) => (
                  <div key={v} className="grid items-center gap-2 sm:grid-cols-[1fr_260px]">
                    <span className="truncate text-[13px] font-medium">{v}</span>
                    <SearchableSelect aria-label={`Lead source for ${v}`} value={sourceMap[v] || ""} onChange={(e) => setSourceMap((m) => ({ ...m, [v]: e.target.value }))} className={selectClass}>
                      <option value="">Choose a source</option>
                      {sources.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
                    </SearchableSelect>
                  </div>
                ))}
              </div>
            ) : null}
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <label htmlFor="imp-source" className="text-[12px] font-medium text-foreground/80">{fileSources.length ? "Source for rows without one" : "Source for every row"}</label>
                <SearchableSelect id="imp-source" value={source} onChange={(e) => setSource(e.target.value)} className={selectClass}>
                  {sources.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
                </SearchableSelect>
              </div>
              <div className="space-y-1.5">
                <label htmlFor="imp-campaign" className="text-[12px] font-medium text-foreground/80">Campaign (optional)</label>
                <Input id="imp-campaign" value={campaign} onChange={(e) => setCampaign(e.target.value)} placeholder={mapped("campaign") ? "For rows without one" : "e.g. Textile Expo 2026"} className="h-9 text-[13px]" />
              </div>
            </div>
          </Card>

          <Card title="Who gets the leads">
            <div role="radiogroup" aria-label="Owner" className="grid gap-2 sm:grid-cols-2">
              <Choice name="owner" value="rules" current={ownerMode} onPick={(v) => setOwnerMode(v as OwnerMode)} label="Follow the routing rules" note={routesAll ? "Round-robin across the sales team, like a new lead" : "The leads are yours"} />
              <Choice name="owner" value="one" current={ownerMode} onPick={(v) => setOwnerMode(v as OwnerMode)} label="One person gets them all">
                <SearchableSelect aria-label="Person" value={ownerId} onChange={(e) => setOwnerId(e.target.value)} className={selectClass}>
                  <option value="">Choose a person</option>
                  {reps.map((r) => <option key={r.id} value={r.id}>{repName(r)}{r.user_role?.name ? ` — ${r.user_role.name}` : ""}</option>)}
                </SearchableSelect>
              </Choice>
              <Choice name="owner" value="turns" current={ownerMode} onPick={(v) => setOwnerMode(v as OwnerMode)} label="Split between people, taking turns">
                <div className="max-h-44 space-y-1 overflow-y-auto">
                  {reps.map((r) => (
                    <label key={r.id} className="flex items-center gap-2 text-[13px]">
                      <input type="checkbox" className="accent-primary" checked={ownerIds.includes(r.id)} onChange={(e) => setOwnerIds((ids) => (e.target.checked ? [...ids, r.id] : ids.filter((x) => x !== r.id)))} />
                      {repName(r)}
                    </label>
                  ))}
                </div>
              </Choice>
              <Choice name="owner" value="column" current={ownerMode} onPick={(v) => setOwnerMode(v as OwnerMode)} label="From the owner column" note={mapped("owner_email") ? "Each row's owner is found by email" : "Match a column to Owner's email first"} disabled={!mapped("owner_email")} />
              {routesAll && <Choice name="owner" value="unassigned" current={ownerMode} onPick={(v) => setOwnerMode(v as OwnerMode)} label="Leave unassigned" note="They wait in the unassigned pool for someone to claim" />}
            </div>
          </Card>

          <Card title="Leads already in the CRM" sub="A row is a duplicate when its email, phone or company matches an existing lead, or an earlier row of the file.">
            <div role="radiogroup" aria-label="Duplicates" className="grid gap-2 sm:grid-cols-2">
              <Choice name="dups" value="skip" current={duplicates} onPick={(v) => setDuplicates(v as "skip" | "create")} label="Skip them" note="Recommended. They're listed so you can check them." />
              <Choice name="dups" value="create" current={duplicates} onPick={(v) => setDuplicates(v as "skip" | "create")} label="Import them anyway" note='Tagged "possible-duplicate" so they can be found and merged' />
            </div>
          </Card>

          <div className="flex flex-wrap items-center justify-between gap-2">
            <Button type="button" variant="outline" onClick={() => setStep(2)}>Back</Button>
            <div className="flex items-center gap-3">
              {choicesProblem && <span className="text-xs text-warning">{choicesProblem}</span>}
              <Button type="button" disabled={!!choicesProblem} onClick={runCheck}>Next: check the rows</Button>
            </div>
          </div>
        </>
      )}

      {step === 4 && (
        <>
          {checking && (
            <Card title="Checking every row…" sub={checkNote ? `${checkNote} Nothing is saved yet.` : "Nothing is saved yet."}><RefreshCw className="size-5 animate-spin text-primary" /></Card>
          )}
          {checked && !done && (
            <Card title={`Check · ${fileName}`} sub="Nothing has been saved yet.">
              <div className="flex flex-wrap gap-2 text-[13px]">
                <StatusPill tone="success">{checked.counts.ready} ready</StatusPill>
                <StatusPill tone="warning">{checked.counts.duplicate} duplicate{checked.counts.duplicate === 1 ? "" : "s"}{duplicates === "skip" ? " (skipped)" : " (imported, tagged)"}</StatusPill>
                <StatusPill tone="danger">{checked.counts.error} with problems (left out)</StatusPill>
              </div>
              {checked.follow_up.total > 0 && (
                <p className="rounded-lg border border-info/30 bg-info-soft px-3 py-2 text-[12.5px] text-foreground">
                  <strong>{checked.follow_up.total} lead{checked.follow_up.total === 1 ? "" : "s"}</strong> will go into the <strong>Follow-up</strong> stage of the funnel.
                  {checked.follow_up.from_priority > 0 && ` ${checked.follow_up.from_priority} of them said "Follow-up" in the Priority column; that is a stage, not a priority, so they start as Warm.`}
                  {" "}They make the list to contact one by one; each moves to Contacted once you reach them.
                </p>
              )}
              <StatusTabs label="Rows" active={view} onChange={setView} tabs={[{ key: "all", label: "All", count: checked.counts.total }, { key: "ready", label: "Ready", count: checked.counts.ready }, { key: "duplicate", label: "Duplicates", count: checked.counts.duplicate }, { key: "error", label: "Problems", count: checked.counts.error }]} />
              <div className="max-h-[420px] overflow-auto">
                <table className="w-full min-w-[720px] border-collapse text-[13px]">
                  <thead className="sticky top-0 bg-card">
                    <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                      <th className="py-1.5 font-semibold">Line</th><th className="py-1.5 font-semibold">Name</th><th className="py-1.5 font-semibold">Company</th><th className="py-1.5 font-semibold">Contact</th><th className="py-1.5 font-semibold">Status</th><th className="py-1.5 font-semibold">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.slice(0, 300).map((r) => (
                      <tr key={r.line} className="border-t border-border/60 align-top">
                        <td className="py-1.5 pr-2 font-mono text-xs text-muted-foreground">{r.line}</td>
                        <td className="py-1.5 pr-2">{cell(r.line, "full_name") || [cell(r.line, "first_name"), cell(r.line, "last_name")].filter(Boolean).join(" ") || "—"}</td>
                        <td className="py-1.5 pr-2">{cell(r.line, "company_name") || "—"}</td>
                        <td className="py-1.5 pr-2 font-mono text-xs">{cell(r.line, "mobile") || cell(r.line, "phone") || cell(r.line, "email") || "—"}</td>
                        <td className="py-1.5 pr-2"><StatusPill tone={STATUS[r.status].tone}>{STATUS[r.status].label}</StatusPill></td>
                        <td className="py-1.5 text-xs text-muted-foreground">{r.problems.join("; ") || [r.stage === "FOLLOW_UP" ? "Follow-up stage" : r.stage && r.stage !== "NEW" ? `${r.stage.charAt(0)}${r.stage.slice(1).toLowerCase()} stage` : "", ...(r.notes || []), r.owner ? `To ${r.owner}` : ""].filter(Boolean).join(" · ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {shown.length > 300 && <p className="py-2 text-xs text-muted-foreground">Showing the first 300 of {shown.length}.</p>}
              </div>
              {progress !== null && (
                <div role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100} aria-label="Import progress" className="h-2 overflow-hidden rounded-full bg-muted">
                  <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
                </div>
              )}
              <div className="flex flex-wrap justify-between gap-2">
                <Button type="button" variant="outline" disabled={progress !== null} onClick={() => setStep(3)}>Back to choices</Button>
                <Button type="button" disabled={progress !== null || checked.will_import === 0} onClick={runImport}>
                  {progress !== null ? `Importing… ${progress}%` : `Import ${checked.will_import} lead${checked.will_import === 1 ? "" : "s"}`}
                </Button>
              </div>
            </Card>
          )}
          {done && (
            <Card title="Import finished">
              <div className="flex items-center gap-2 text-[15px] font-semibold text-success"><CheckCircle2 className="size-5" />{done.created} lead{done.created === 1 ? "" : "s"} added</div>
              <p className="text-[13px] text-muted-foreground">{done.skipped} duplicate{done.skipped === 1 ? "" : "s"} skipped · {done.failed} row{done.failed === 1 ? "" : "s"} with problems</p>
              <div className="flex flex-wrap gap-2">
                <Button type="button" onClick={() => navigate("/leads")}>Go to leads</Button>
                {done.problems.size > 0 && <Button type="button" variant="outline" onClick={downloadProblems}><Download className="mr-1.5 size-4" />Download the {done.problems.size} rows not imported</Button>}
                {done.created > 0 && <Button type="button" variant="outline" onClick={() => undo(done.id)}><Undo2 className="mr-1.5 size-4" />Undo this import</Button>}
                <Button type="button" variant="ghost" onClick={() => { setStep(1); setDone(null); setChecked(null); }}>Import another file</Button>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
